/**
 * Phase 4 · Kitchen display: station-filtered ticket board with live timers
 * and one-tap status progression (new → preparing → ready → served).
 */
import { useCallback, useEffect, useState } from 'react';
import { Check, ChefHat, Flame } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useUi } from '../store/ui';
import { StatusBadge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';

interface Ticket {
  id: string; station: string; status: string; opened_at: string;
  order_number: string; table_label: string | null;
  items: { id: string; name: string; quantity: number; notes: string | null; status: string }[];
}

const STATIONS = ['all', 'kitchen', 'bar', 'grill', 'dessert'];

function age(openedAt: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(openedAt).getTime()) / 60000));
  return `${mins}m`;
}

export function KdsPage() {
  const toast = useUi((s) => s.toast);
  const [station, setStation] = useState('all');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await http.get<{ tickets: Ticket[] }>('/orders/kds/tickets', station === 'all' ? undefined : { station });
      setTickets(r.tickets);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [station, toast]);
  // Poll the ticket board so new/updated dockets appear without a manual reload.
  useEffect(() => { void load(); const t = setInterval(() => { void load(); }, 30000); return () => clearInterval(t); }, [load]);

  const advance = async (t: Ticket) => {
    const next = t.status === 'new' ? 'preparing' : t.status === 'preparing' ? 'ready' : 'served';
    try {
      await http.post(`/orders/kds/tickets/${t.id}/status`, { status: next });
      await load();
    } catch (e) {
      toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-ink"><ChefHat size={20} /> Kitchen display</h1>
        <div className="flex gap-1 rounded-lg bg-surface-1 p-1">
          {STATIONS.map((s) => (
            <button key={s} onClick={() => setStation(s)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize ${station === s ? 'bg-surface-0 text-ink shadow-sm' : 'text-ink-muted hover:text-ink'}`}>
              {s}
            </button>
          ))}
        </div>
      </header>

      {loading ? <p className="text-sm text-ink-muted">Loading tickets…</p>
        : !tickets.length ? <p className="text-sm text-ink-muted">No open tickets — the kitchen is clear. 🎉</p>
        : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {tickets.map((t) => (
              <article key={t.id} className={`rounded-xl border p-3 ${t.status === 'ready' ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'border-line bg-surface-0'}`}>
                <header className="mb-2 flex items-center justify-between">
                  <p className="font-semibold text-ink">#{t.order_number}{t.table_label ? ` · ${t.table_label}` : ''}</p>
                  <span className="flex items-center gap-2 text-xs text-ink-muted"><StatusBadge status={t.status} /> <span className="font-mono">{age(t.opened_at)}</span></span>
                </header>
                <ul className="mb-3 space-y-1 text-sm">
                  {t.items.map((i) => (
                    <li key={i.id} className="flex justify-between gap-2 text-ink">
                      <span>{i.quantity}× {i.name}{i.notes ? <em className="text-xs text-ink-muted"> — {i.notes}</em> : null}</span>
                      <StatusBadge status={i.status} />
                    </li>
                  ))}
                </ul>
                <footer className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-ink-muted">{t.station}</span>
                  {t.status !== 'served' && (
                    <Button size="sm" variant={t.status === 'preparing' ? 'primary' : 'secondary'} onClick={() => void advance(t)}>
                      {t.status === 'new' ? <><Flame size={14} /> Start</> : t.status === 'preparing' ? <><Check size={14} /> Ready</> : 'Served'}
                    </Button>
                  )}
                </footer>
              </article>
            ))}
          </div>
        )}
    </div>
  );
}
